/**
 * Google Ads Conversion Tracking Utility
 *
 * IMPORTANT:
 * Google Ads gtag tracking uses the Ads "conversion tracking ID" (AW-XXXXXXXXX),
 * NOT the Google Ads customer ID (e.g. 550-846-6310).
 *
 * We confirmed via Google Ads API `conversion_action.tag_snippets` that the DNA Kit
 * Purchase action fires with `send_to: 'AW-989780990/SI1sCJyv_vEbEP63-9cD'`.
 */

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

// Google Ads conversion tracking ID (NOT the customer ID)
export const GOOGLE_ADS_ID = 'AW-989780990';

// Conversion Labels - configured in Google Ads
export const CONVERSION_LABELS = {
  // DNA Kit Purchase (conversion_action.id = 7486805916)
  // send_to: 'AW-989780990/SI1sCJyv_vEbEP63-9cD'
  DNA_KIT_PURCHASE: 'SI1sCJyv_vEbEP63-9cD',
} as const;

/**
 * Track a Google Ads conversion event
 * @param conversionLabel - The conversion label from CONVERSION_LABELS
 * @param value - Optional conversion value in USD
 * @param transactionId - Optional unique transaction ID to prevent duplicate conversions
 */
export const trackGoogleAdsConversion = (
  conversionLabel: string,
  value?: number,
  transactionId?: string
): void => {
  if (typeof window === 'undefined' || !window.gtag) {
    console.debug('[GoogleAds] gtag not available, skipping conversion tracking');
    return;
  }

  const conversionData: Record<string, any> = {
    send_to: `${GOOGLE_ADS_ID}/${conversionLabel}`,
  };

  if (value !== undefined) {
    conversionData.value = value;
    conversionData.currency = 'USD';
  }

  if (transactionId) {
    conversionData.transaction_id = transactionId;
  }

  console.debug('[GoogleAds] Tracking conversion:', conversionData);
  window.gtag('event', 'conversion', conversionData);
};

/**
 * Track a DNA Kit purchase conversion
 * @param purchaseValue - The purchase value in USD (default: $199)
 * @param sessionId - Stripe session ID to use as transaction ID
 */
export const trackDNAKitPurchase = (
  purchaseValue: number = 199,
  sessionId?: string
): void => {
  trackGoogleAdsConversion(
    CONVERSION_LABELS.DNA_KIT_PURCHASE,
    purchaseValue,
    sessionId
  );
};

/**
 * Track a page view for remarketing/audience building
 * This is automatically handled by gtag config, but can be called manually if needed
 */
export const trackPageView = (pagePath?: string): void => {
  if (typeof window === 'undefined' || !window.gtag) {
    return;
  }

  const pageData: Record<string, any> = {
    send_to: GOOGLE_ADS_ID,
  };

  if (pagePath) {
    pageData.page_path = pagePath;
  }

  window.gtag('event', 'page_view', pageData);
};

/**
 * Send enhanced conversion data (for improved conversion matching)
 * Call this with user data when they complete a purchase
 * @param userData - User data for enhanced conversions
 */
export const setUserData = (userData: {
  email?: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
  address?: {
    street?: string;
    city?: string;
    region?: string;
    postalCode?: string;
    country?: string;
  };
}): void => {
  if (typeof window === 'undefined' || !window.gtag) {
    return;
  }

  const enhancedData: Record<string, any> = {};

  if (userData.email) {
    enhancedData.email = userData.email.toLowerCase().trim();
  }
  if (userData.phone) {
    enhancedData.phone_number = userData.phone;
  }
  if (userData.firstName || userData.lastName) {
    enhancedData.address = {
      first_name: userData.firstName,
      last_name: userData.lastName,
      ...userData.address,
    };
  }

  if (Object.keys(enhancedData).length > 0) {
    window.gtag('set', 'user_data', enhancedData);
  }
};
